import { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Notification from "../../../components/toast";
import reqVenda from "../../../services/reqMultiTenant/reqVenda";
import reqVendaItem from "../../../services/reqMultiTenant/reqVendaItem";
import reqContaEntrada from "../../../services/reqMultiTenant/reqContaEntrada";
import { ContextGlobal } from "../../../context";

export function useAlterarVenda(id, onAlteracao) {
	const navigate = useNavigate();
	const { bankData, fetchBank } = useContext(ContextGlobal);

	const [venda, setVenda] = useState(null);
	const [itens, setItens] = useState([]);
	const [parcelas, setParcelas] = useState([]);
	const [complementos, setComplementos] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const [pendingClient, setPendingClient] = useState(null);
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);

	const [isRefazerOpen, setIsRefazerOpen] = useState(false); // Alteração Geral da Venda (produtos + pagamento)
	const [isParcelasOpen, setIsParcelasOpen] = useState(false); // Refazer Parcelas isolado (já pronto)

	// O gerador de parcelas padrão (componente compartilhado) exige a lista de contas de
	// recebimento (bankData) do ContextGlobal — garante que está carregada antes de abrir o modal,
	// mesmo padrão usado em account-entry/account-out/leads.
	useEffect(() => {
		if (bankData.length === 0) fetchBank();
	}, []);

	// silent: quando true, atualiza os dados em segundo plano sem acionar a tela cheia de loading
	const fetchDadosReaisDoServidor = useCallback(
		async (vendaId, { silent = false } = {}) => {
			if (!silent) setLoading(true);
			try {
				// Busca paralela para máxima performance no Linux (Horse)
				const [vendaRes, itensRes, parcelasRes, complementosRes] = await Promise.all([
					reqVenda.listCupom(vendaId),
					reqVendaItem.listByVenda(vendaId),
					reqContaEntrada.listByVenda(vendaId),
					reqContaEntrada.listVendaComplementos(vendaId),
				]);

				setVenda(vendaRes.data?.[0] || null); // O cupom costuma retornar um array com 1 obj
				setItens(itensRes.data || []);
				setParcelas(parcelasRes.data || []);
				setComplementos(complementosRes.data || []);
			} catch (error) {
				const msgErro =
					error?.response?.data?.error ||
					error?.message ||
					"Erro ao buscar dados reais da venda no servidor.";
				Notification.showToast({ message: msgErro, type: "error" });
				console.error("Erro no Promise.all da Venda:", error);
				// Retorna para a tela anterior se houver falha (Account Entry)
				navigate(-1);
			} finally {
				if (!silent) setLoading(false);
			}
		},
		[navigate],
	);

	// Recebe o cliente selecionado no modal e abre o dialog de confirmação
	const onSelectClient = useCallback((item) => {
		setPendingClient(item);
		setIsConfirmOpen(true);
	}, []);

	// Confirma a alteração do cliente no backend, atualizando o estado e recarregando os dados reais
	const handleConfirmClient = useCallback(async () => {
		if (!pendingClient) return;

		try {
			// Faz a chamada de PATCH para atualizar o cliente tanto na Venda quanto na Conta_Entrada
			await reqVenda.updateCliente(id, pendingClient.id_cliente);

			Notification.showToast({ message: "Cliente alterado com sucesso!", type: "success" });
			
			if (onAlteracao) onAlteracao();

			// Recarrega só os dados (sem acionar a tela cheia de loading) para refletir na tela
			await fetchDadosReaisDoServidor(id, { silent: true });
		} catch (error) {
			const msgErro =
				error?.response?.data || error?.message || "Erro ao salvar a alteração do cliente no servidor.";
			Notification.showToast({ message: msgErro, type: "error" });
			console.error("Erro ao alterar o cliente da venda:", error);
		} finally {
			setIsConfirmOpen(false);
			setPendingClient(null);

			// Fecha o modal de seleção de clientes
			const event = new CustomEvent("modalSelectClientVenda");
			window.dispatchEvent(event);
		}
	}, [id, pendingClient, fetchDadosReaisDoServidor, onAlteracao]);

	// Cancela a alteração do cliente
	const handleCancelClient = useCallback(() => {
		setIsConfirmOpen(false);
		setPendingClient(null);
	}, []);

	// Salva as novas parcelas regeradas/alteradas no backend
	const handleSaveParcelas = useCallback(
		async (tipo, novasParcelas) => {
			setSaving(true);
			try {
				await reqVenda.saveParcelas(id, { tipo, parcelas: novasParcelas });
				Notification.showToast({ message: "Parcelas alteradas com sucesso!", type: "success" });
				
				if (onAlteracao) onAlteracao();
				
				await fetchDadosReaisDoServidor(id);
				setIsParcelasOpen(false);
			} catch (error) {
				const msgErro =
					error?.response?.data?.error ||
					error?.response?.data ||
					error?.message ||
					"Erro ao salvar as novas parcelas no servidor.";
				Notification.showToast({ message: msgErro, type: "error" });
				console.error("Erro ao salvar parcelas:", error);
			} finally {
				setSaving(false);
			}
		},
		[id, fetchDadosReaisDoServidor, onAlteracao],
	);

	useEffect(() => {
		if (id) {
			fetchDadosReaisDoServidor(id);
		}
	}, [id, fetchDadosReaisDoServidor]);

	// Busca os dados atualizados do servidor antes de abrir a Alteração Geral — garante que o
	// wizard sempre começa com o retrato atual do banco, nunca com dado de uma sessão anterior
	const handleAbrirAlteracaoGeral = useCallback(async () => {
		await fetchDadosReaisDoServidor(id, { silent: true });
		setIsRefazerOpen(true);
	}, [id, fetchDadosReaisDoServidor]);

	// Chamado pelo wizard após salvar a Alteração Geral no backend — recarrega os dados reais
	// (sem tela cheia de loading) pra refletir o novo retrato da venda e fecha o modal.
	const handleAlteracaoGeralSalva = useCallback(async () => {
		await fetchDadosReaisDoServidor(id, { silent: true });
		
		if (onAlteracao) onAlteracao();
		
		setIsRefazerOpen(false);
	}, [id, fetchDadosReaisDoServidor, onAlteracao]);

	return {
		venda,
		itens,
		parcelas,
		complementos,
		loading,
		saving,
		pendingClient,
		isConfirmOpen,
		onSelectClient,
		handleConfirmClient,
		handleCancelClient,
		isRefazerOpen,
		setIsRefazerOpen,
		handleAbrirAlteracaoGeral,
		handleAlteracaoGeralSalva,
		isParcelasOpen,
		setIsParcelasOpen,
		handleSaveParcelas,
		setVenda,
		setItens,
		setParcelas,
		setComplementos,
	};
}
